import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { removeUserData, userLogout } from "../store/slices/userSlice";
import { STATUS_CODES } from "../utils/statusCode";
import { useLoader } from "../contexts/LoaderContext";
import Toster from "../utils/Toaster";
import EditProfileModal from "./AppComponents/EditProfile";
import ChangePasswordModal from "./AppComponents/ChangePasswordModal";

const Header = () => {
  const userData = useSelector((state) => state.user.userData);
  const profileImg = useSelector((state) => state.user.userData?.profileImg);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const containerRef = useRef(null);
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const mainNavLinks = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/officers", label: "Officers" },
    { path: "/broadcast-notification", label: "Broadcast" },
    { path: "/blogs", label: "Blogs" },
    { path: "/subscription", label: "Subscriptions" },
    { path: "/referrals", label: "Referrals" },
    { path: "/operations", label: "Operations & Alerts" },
  ];

  const profileMenuLinks = [
    { path: "/content?pageName=aboutUs", label: "About Us" },
    { path: "/content?pageName=termsCondition", label: "Terms & Conditions" },
    { path: "/content?pageName=privacyPolicy", label: "Privacy Policy" },
  ];

  useEffect(() => {
    const closeMenu = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  useEffect(() => setIsMenuOpen(false), [location.pathname, location.search]);

  const logoutClear = async () => {
    setLoading(true);
    try {
      const response = await dispatch(userLogout());
      if (response.payload?.code === STATUS_CODES.SUCCESS) {
        Toster("Logged out successfully", "success");
      }
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      // Logging out must always clear the local session. An expired server
      // token can make the logout API return 401, but should never trap the
      // administrator inside a dashboard that cannot load data.
      localStorage.removeItem("userToken");
      localStorage.removeItem("persist:root");
      dispatch(removeUserData({}));
      navigate("/login", { replace: true });
      setLoading(false);
    }
  };

  const initials = (userData?.firstName || userData?.name || "NS")
    .split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();

  const navClass = (path) => {
    const active = location.pathname === path;
    return "rounded-full px-4 py-2 text-sm font-medium transition " +
      (active ? "bg-[#67A3FA] text-[#07090D]" : "text-white/65 hover:bg-white/[0.07] hover:text-white");
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#07090D]/95 text-white shadow-[0_16px_50px_rgba(0,0,0,0.28)] backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] w-full max-w-[1600px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link className="flex min-w-0 items-center gap-3" to="/dashboard" aria-label="NinetySix admin dashboard">
            <span className="flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-white shadow-[0_10px_30px_rgba(103,163,250,0.12)]">
              <img src="/logo.png" alt="NinetySix" className="h-9 w-9 object-contain" />
            </span>
            <span className="hidden min-w-0 sm:block">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#67A3FA]">NinetySix</span>
              <span className="block truncate text-sm font-medium text-white/75">Command Center</span>
            </span>
          </Link>

          {userData?.token && (
            <div className="flex items-center gap-3" ref={containerRef}>
              <nav className="hidden items-center gap-1 lg:flex" aria-label="Admin navigation">
                {mainNavLinks.map((link) => (
                  <Link key={link.path} to={link.path} className={navClass(link.path)}>{link.label}</Link>
                ))}
              </nav>

              <button type="button" onClick={() => setIsMenuOpen((open) => !open)}
                className="flex h-11 items-center gap-2 rounded-full border border-white/10 bg-white/[0.055] pl-1.5 pr-3 text-sm text-white transition hover:border-[#67A3FA]/40 hover:bg-white/[0.09]"
                aria-expanded={isMenuOpen} aria-label="Open admin menu">
                {profileImg ? (
                  <img src={profileImg} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#67A3FA] text-xs font-bold text-[#07090D]">{initials}</span>
                )}
                <span className="hidden sm:inline">Menu</span>
                <svg className={"h-4 w-4 transition " + (isMenuOpen ? "rotate-180" : "")} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {isMenuOpen && (
                <div className="absolute right-4 top-[68px] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-white/10 bg-[#10131A] p-2 shadow-[0_30px_80px_rgba(0,0,0,0.55)] sm:right-6 lg:right-8">
                  <div className="border-b border-white/10 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#67A3FA]">Admin account</p>
                    <p className="mt-1 truncate text-sm text-white/65">{userData?.email || "NinetySix Technologies LLC"}</p>
                  </div>
                  <nav className="grid gap-1 py-2 lg:hidden" aria-label="Mobile admin navigation">
                    {mainNavLinks.map((link) => (
                      <Link key={link.path} to={link.path}
                        className={"rounded-2xl px-4 py-3 text-sm " + (location.pathname === link.path ? "bg-[#67A3FA] font-semibold text-[#07090D]" : "text-white/70 hover:bg-white/[0.06] hover:text-white")}>
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="grid gap-1 border-t border-white/10 pt-2 lg:border-t-0">
                    {profileMenuLinks.map((link) => (
                      <Link key={link.path} to={link.path} className="rounded-2xl px-4 py-3 text-sm text-white/65 hover:bg-white/[0.06] hover:text-white">{link.label}</Link>
                    ))}
                    <button type="button" className="rounded-2xl px-4 py-3 text-left text-sm text-white/65 hover:bg-white/[0.06] hover:text-white" onClick={() => { setEditModalOpen(true); setIsMenuOpen(false); }}>Edit profile</button>
                    <button type="button" className="rounded-2xl px-4 py-3 text-left text-sm text-white/65 hover:bg-white/[0.06] hover:text-white" onClick={() => { setChangePasswordModalOpen(true); setIsMenuOpen(false); }}>Change password</button>
                    <button type="button" className="rounded-2xl px-4 py-3 text-left text-sm font-medium text-red-300 hover:bg-red-500/10" onClick={logoutClear}>Log out</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {isEditModalOpen && <EditProfileModal isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)} />}
      <ChangePasswordModal isOpen={isChangePasswordModalOpen} setChangePasswordModalOpen={setChangePasswordModalOpen} />
    </>
  );
};

export default Header;
